<template>
  <div class="course-page">
    <div class="course-page__inner">
      <!-- carregando -->
      <page-skeleton v-if="loading" variant="cards" class="mt-4" />

      <template v-else>
        <!-- Card 86akfygzr: aluno sem professor aguardando a designação do
             tutor. Não é erro nem redirecionamento — não há para onde mandá-lo;
             a tela explica o que falta e o que já dá para fazer. -->
        <div v-if="needsTutor" class="await-tutor">
          <v-icon size="40" color="#A826E1">mdi-account-clock-outline</v-icon>
          <h2 class="await-tutor__title">
            {{ $t("customerCourse.tutorTitle") }}
          </h2>
          <p class="await-tutor__text">
            {{ $t("customerCourse.tutorText") }}
          </p>
          <p class="await-tutor__hint">
            {{ $t("customerCourse.tutorHint") }}
          </p>
          <v-btn
            rounded
            depressed
            dark
            color="#A826E1"
            @click="$router.push('/connect/aluno/perfil')"
          >
            {{ $t("customerCourse.goProfile") }}
          </v-btn>
        </div>

        <!-- CATÁLOGO INTEIRO: a escada completa (grade de modulos) -->
        <template v-else-if="fullCatalog">
          <CourseHeader
            :eyebrow="$t('customerCourse.studentEyebrow')"
            :title="$t('customerCourse.yourModules')"
            :subtitle="catalogSubtitle"
          />

          <!-- Card 86akqwgkw: busca de aula pelo nome em todos os módulos. -->
          <CourseFinder v-if="modules.length > 0" :search.sync="search" />

          <!-- Card 17trt6jfxtn (retorno de 30/10): o que o professor indicou,
               logo abaixo do campo de busca. -->
          <TeacherExtrasList v-if="showExtras" :items="extras" />

          <LessonSearchResults
            v-if="searching"
            :results="foundLessons"
            @open="openFoundLesson"
          />

          <p v-if="searching && visibleModules.length" class="sec-label">
            {{ $t("courseIndex.modules") }}
          </p>
          <div v-if="visibleModules.length > 0" class="mgrid">
            <ModuleCard
              v-for="(item, index) in visibleModules"
              :key="item.id || index"
              :course="item"
              role="student"
              @open="goToModule"
            />
          </div>
          <div v-else-if="!searching" class="course-page__state">
            <span>{{ $t("customerCourse.noModules") }}</span>
          </div>
        </template>

        <!-- ALUNO COM PROFESSOR: apenas o modulo do seu nivel -->
        <template v-else-if="course.id">
          <ModuleHero
            :module="course"
            :badge="$t('customerCourse.yourLevel')"
          />

          <!-- Card 86akqwgkw: busca de aula pelo nome no módulo do nível. -->
          <CourseFinder
            v-if="leassons.length > 0"
            :search.sync="search"
            :summary="lessonSummary"
          />

          <!-- Card 17trt6jfxtn (retorno de 30/10): o que o professor indicou,
               logo abaixo do campo de busca. -->
          <TeacherExtrasList v-if="showExtras" :items="extras" />

          <p class="sec-label">{{ $t("customerCourse.levelLessons") }}</p>
          <div v-if="visibleLessons.length > 0" class="list">
            <LessonRow
              v-for="row in visibleLessons"
              :key="row.item.id || row.index"
              :item="row.item"
              :accent="accent"
              :order="row.index + 1"
              @open="goToCourse"
            />
          </div>
          <div v-else-if="searching" class="course-page__state">
            <span>{{ $t("customerCourse.noMatch") }}</span>
          </div>
          <div v-else class="course-page__state">
            <span>{{ $t("customerCourse.noLessons") }}</span>
          </div>
        </template>

        <!-- sem conteudo -->
        <div v-else class="course-page__state">
          <span>{{ $t("customerCourse.noContent") }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CourseHeader from "@/components/connect/course/CourseHeader.vue";
import ModuleCard from "@/components/connect/course/ModuleCard.vue";
import ModuleHero from "@/components/connect/course/ModuleHero.vue";
import LessonRow from "@/components/connect/course/LessonRow.vue";
import CourseFinder from "@/components/connect/course/CourseFinder.vue";
import TeacherExtrasList from "@/components/connect/course/TeacherExtrasList.vue";
import LessonSearchResults from "@/components/connect/course/LessonSearchResults.vue";
import { tierColor, normalizeSearch, findLessons } from "@/shared/courseTiers";
import pitchCustomerApi from "@/shared/pitchCustomerApi";

export default {
  name: "CourseArea",
  components: {
    CourseHeader,
    ModuleCard,
    ModuleHero,
    LessonRow,
    CourseFinder,
    LessonSearchResults,
    TeacherExtrasList,
  },
  async created() {
    this.loading = true;

    // Falhar aqui só esconde o atalho: o curso carrega do mesmo jeito.
    pitchCustomerApi
      .getTeacherExtras()
      .then((res) => {
        this.extras = res.data?.data || [];
      })
      .catch(() => null);

    await pitchCustomerApi
      .getCourse()
      .then((response) => {
        this.loading = false;

        // Card 86akfygzr: solo sem tutor designado. Antes do fullCatalog
        // porque é justamente o caso em que o catálogo fica retido.
        if (response.data.needsTutor) {
          this.needsTutor = true;
          this.solo = true;
          return;
        }

        // Catalogo inteiro: aluno sem professor e tambem quem veio do plano solo
        // e ja se vinculou a um professor -- este ultimo mantem o material que
        // ja acessava, entao a grade continua sendo a de todos os modulos.
        if (response.data.fullCatalog) {
          this.fullCatalog = true;
          this.solo = !!response.data.solo;
          this.modules = response.data.modules || [];

          this.$root.$emit("display-alert", {
            text: this.$t("customerCourse.fullAccess"),
            type: 0,
            timeout: 1500,
          });
          return;
        }

        // Aluno com professor ainda sem turma: o backend responde needsMatch em
        // vez de erro. Direciona ao fluxo de turmas/match sem quebrar a tela.
        if (response.data.needsMatch) {
          this.$root.$emit("display-alert", {
            text: this.$t("customerCourse.matchFirst"),
            type: 0,
            timeout: 1500,
          });

          this.$router.push("/connect/aluno/turmas");
          return;
        }

        this.course = response.data.module;
        this.leassons = response.data.module.classes || [];

        this.$root.$emit("display-alert", {
          text: this.$t("customerCourse.welcome", { title: this.course.title }),
          type: 0,
          timeout: 1000,
        });
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 400) {
          this.$root.$emit("display-alert", {
            text: this.$t("customerCourse.matchFirst"),
            type: 0,
            timeout: 1500,
          });

          this.$router.push("/connect/aluno/turmas");
        } else {
          this.$root.$emit("display-alert", {
            text: this.$t("customerCourse.loadFailed"),
            type: 2,
            timeout: 1000,
          });

          this.leassons = [];
          this.loading = false;
        }
      });
  },
  data() {
    return {
      loading: false,
      solo: false,
      fullCatalog: false,
      // Card 86akfygzr: aluno solo ainda sem tutor designado.
      needsTutor: false,
      // Card 17trt6jfxtn: conteúdo extra indicado pelo professor.
      extras: [],
      modules: [],
      course: {},
      leassons: [],
      search: "",
    };
  },
  computed: {
    accent() {
      return tierColor(this.course.level);
    },
    // Quem já tem professor mas manteve o catálogo precisa saber que os dois
    // convivem: o módulo do nível é onde a turma anda, o resto segue liberado.
    catalogSubtitle() {
      return this.solo
        ? this.$t("customerCourse.soloSubtitle")
        : this.$t("customerCourse.keptSubtitle");
    },
    // Card 86akqwgkw: busca de aula pelo nome.
    // Nunca para quem espera o tutor; durante a busca os resultados mandam.
    showExtras() {
      return !this.needsTutor && !this.searching && this.extras.length > 0;
    },
    searching() {
      return !!normalizeSearch(this.search);
    },
    foundLessons() {
      return findLessons(this.modules, this.search);
    },
    // Na busca, ficam os módulos cujo título bate ou que têm aula encontrada.
    visibleModules() {
      if (!this.searching) return this.modules;

      const term = normalizeSearch(this.search);
      const withLesson = new Set(
        this.foundLessons.map((item) => String(item.module.id))
      );
      return this.modules.filter(
        (item) =>
          withLesson.has(String(item.id)) ||
          normalizeSearch(item.title).includes(term)
      );
    },
    // Módulo único: filtra a lista mantendo a posição original da aula.
    visibleLessons() {
      const term = normalizeSearch(this.search);
      return this.leassons
        .map((item, index) => ({ item, index }))
        .filter(
          (row) => !term || normalizeSearch(row.item.title).includes(term)
        );
    },
    lessonSummary() {
      if (!this.searching) return "";
      const total = this.visibleLessons.length;
      return this.$tc("course.lessonsFound", total, { count: total });
    },
  },
  methods: {
    openFoundLesson({ lesson }) {
      this.$router.push({ path: "/connect/aluno/curso/" + lesson.id });
    },
    goToModule(item) {
      this.$router.push({
        path: "/connect/aluno/curso/modulo/" + item.id,
      });
    },
    async goToCourse(item) {
      await this.$router.push({
        path: "/connect/aluno/curso/" + item.id,
        params: item.id,
      });
    },
  },
};
</script>

<style scoped>
.course-page {
  padding: clamp(20px, 4vw, 44px) clamp(16px, 4vw, 40px) 72px;
}
.course-page__inner {
  max-width: 1180px;
  margin: 0 auto;
}
.mgrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
@media (max-width: 900px) {
  .mgrid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .mgrid {
    grid-template-columns: 1fr;
  }
}
.sec-label {
  margin: 0 0 18px;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--dk-faint, #9a96a6);
}
.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.course-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink-soft, #6e6a79);
  text-align: center;
}

/* Espera do tutor: é um estado da conta, não uma falha — o tom acompanha. */
.await-tutor {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 560px;
  margin: 48px auto;
  padding: 36px 28px;
  border: 1px solid var(--dk-line, #eae7f0);
  border-radius: 20px;
  background: var(--dk-surface, #fff);
  font-family: "Poppins", sans-serif;
  text-align: center;
}
.await-tutor__title {
  margin: 4px 0 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}
.await-tutor__text {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--dk-ink-soft, #6e6a79);
}
.await-tutor__hint {
  margin: 0 0 10px;
  font-size: 0.85rem;
  color: var(--dk-muted, #8a8494);
}
</style>
